import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../hooks/useTranslation';

interface VideoTrimEditorProps {
  videoFile: File;
  onSave: (editedVideo: File) => void;
  onCancel: () => void;
}

const VideoTrimEditor: React.FC<VideoTrimEditorProps> = ({ videoFile, onSave, onCancel }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const url = URL.createObjectURL(videoFile);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [videoFile]);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const videoDuration = videoRef.current.duration;
      setDuration(videoDuration);
      // Only set endTime if it's still 0 (initial load)
      if (endTime === 0) {
        setEndTime(Math.min(videoDuration, 60)); // Max 60 seconds
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      
      // Stop at end time
      if (videoRef.current.currentTime >= endTime) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handlePlay = () => {
    if (videoRef.current) {
      if (currentTime >= endTime) {
        videoRef.current.currentTime = startTime;
      }
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSave = async () => {
    if (!videoRef.current) return;
    
    setIsProcessing(true);
    
    try {
      // Create a new file with trim metadata
      const trimData = {
        startTime,
        endTime,
        originalDuration: duration
      };
      
      // Create a new file with trim metadata (in real implementation, this would be processed)
      const processedFile = new File([videoFile], videoFile.name, {
        type: videoFile.type,
        lastModified: Date.now(),
      });
      
      // Add trim metadata to the file object (this is just for demo)
      (processedFile as any).trimData = trimData;
      
      onSave(processedFile);
    } catch (error) {
      console.error('Error processing video:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const trimmedDuration = endTime - startTime;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-md font-semibold text-gray-900 dark:text-white">
              برش ویدیو
            </h2>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Single Column Layout */}
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Video Player */}
            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                src={videoUrl}
                className="w-full h-auto max-h-80 object-contain"
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
              />
              
              {/* Play/Pause Button */}
              <button
                onClick={isPlaying ? handlePause : handlePlay}
                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 hover:bg-opacity-40 transition-all"
              >
                {!isPlaying && (
                  <div className="bg-white bg-opacity-90 rounded-full p-3">
                    <svg className="w-6 h-6 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                )}
              </button>
            </div>

            {/* Video Info */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-gray-600 dark:text-gray-400">
                  <span>مدت کل: </span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatTime(duration)}</span>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  <span>انتخاب شده: </span>
                  <span className="font-medium text-blue-600 dark:text-blue-400">{formatTime(trimmedDuration)}</span>
                </div>
              </div>
            </div>

            {/* Interactive Timeline */}
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>شروع: {formatTime(startTime)}</span>
                <span>پایان: {formatTime(endTime)}</span>
              </div>
              
              {duration > 0 ? (
                <div 
                  className="relative h-12 bg-gray-200 dark:bg-gray-700 rounded-lg cursor-pointer"
                  onClick={(e) => {
                    if (duration > 0 && e.target === e.currentTarget) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const clickTime = (clickX / rect.width) * duration;
                      handleSeek(clickTime);
                    }
                  }}
                >
                  {/* Selected range */}
                  <div
                    className="absolute top-0 h-12 bg-blue-500 bg-opacity-30 rounded-lg"
                    style={{
                      left: `${(startTime / duration) * 100}%`,
                      width: `${((endTime - startTime) / duration) * 100}%`,
                    }}
                  />
                  
                  {/* Start handle */}
                  <div
                    className="absolute top-1 w-4 h-10 bg-blue-500 rounded cursor-ew-resize shadow-lg hover:bg-blue-600 transition-colors flex items-center justify-center z-10 touch-none"
                    style={{ left: `${(startTime / duration) * 100}%`, transform: 'translateX(-50%)' }}
                    onMouseDown={(e) => {
                      if (duration <= 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const timelineElement = e.currentTarget.parentElement!;
                      const rect = timelineElement.getBoundingClientRect();
                      
                      const handleMove = (clientX: number) => {
                        const mouseX = clientX - rect.left;
                        const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
                        const newStart = percentage * duration;
                        const clampedStart = Math.max(0, Math.min(endTime - 0.5, newStart));
                        setStartTime(clampedStart);
                        
                        // Update video position while dragging
                        if (videoRef.current) {
                          videoRef.current.currentTime = clampedStart;
                        }
                      };
                      
                      const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
                      const handleTouchMove = (e: TouchEvent) => {
                        e.preventDefault();
                        handleMove(e.touches[0].clientX);
                      };
                      
                      const handleEnd = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleEnd);
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleEnd);
                        document.body.style.userSelect = '';
                        document.body.style.cursor = '';
                      };
                      
                      document.body.style.userSelect = 'none';
                      document.body.style.cursor = 'ew-resize';
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleEnd);
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleEnd);
                    }}
                    onTouchStart={(e) => {
                      if (duration <= 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const timelineElement = e.currentTarget.parentElement!;
                      const rect = timelineElement.getBoundingClientRect();
                      
                      const handleMove = (clientX: number) => {
                        const mouseX = clientX - rect.left;
                        const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
                        const newStart = percentage * duration;
                        const clampedStart = Math.max(0, Math.min(endTime - 0.5, newStart));
                        setStartTime(clampedStart);
                        
                        if (videoRef.current) {
                          videoRef.current.currentTime = clampedStart;
                        }
                      };
                      
                      const handleTouchMove = (e: TouchEvent) => {
                        e.preventDefault();
                        handleMove(e.touches[0].clientX);
                      };
                      
                      const handleTouchEnd = () => {
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleTouchEnd);
                      };
                      
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleTouchEnd);
                    }}
                  >
                    <div className="w-1 h-6 bg-white rounded-full opacity-80"></div>
                  </div>
                  
                  {/* End handle */}
                  <div
                    className="absolute top-1 w-4 h-10 bg-blue-500 rounded cursor-ew-resize shadow-lg hover:bg-blue-600 transition-colors flex items-center justify-center z-10 touch-none"
                    style={{ left: `${(endTime / duration) * 100}%`, transform: 'translateX(-50%)' }}
                    onMouseDown={(e) => {
                      if (duration <= 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const timelineElement = e.currentTarget.parentElement!;
                      const rect = timelineElement.getBoundingClientRect();
                      
                      const handleMove = (clientX: number) => {
                        const mouseX = clientX - rect.left;
                        const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
                        const newEnd = percentage * duration;
                        const clampedEnd = Math.max(startTime + 0.5, Math.min(duration, newEnd));
                        setEndTime(clampedEnd);
                        
                        // Update video position while dragging
                        if (videoRef.current) {
                          videoRef.current.currentTime = clampedEnd;
                        }
                      };
                      
                      const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
                      const handleTouchMove = (e: TouchEvent) => {
                        e.preventDefault();
                        handleMove(e.touches[0].clientX);
                      };
                      
                      const handleEnd = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleEnd);
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleEnd);
                        document.body.style.userSelect = '';
                        document.body.style.cursor = '';
                      };
                      
                      document.body.style.userSelect = 'none';
                      document.body.style.cursor = 'ew-resize';
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleEnd);
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleEnd);
                    }}
                    onTouchStart={(e) => {
                      if (duration <= 0) return;
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const timelineElement = e.currentTarget.parentElement!;
                      const rect = timelineElement.getBoundingClientRect();
                      
                      const handleMove = (clientX: number) => {
                        const mouseX = clientX - rect.left;
                        const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
                        const newEnd = percentage * duration;
                        const clampedEnd = Math.max(startTime + 0.5, Math.min(duration, newEnd));
                        setEndTime(clampedEnd);
                        
                        if (videoRef.current) {
                          videoRef.current.currentTime = clampedEnd;
                        }
                      };
                      
                      const handleTouchMove = (e: TouchEvent) => {
                        e.preventDefault();
                        handleMove(e.touches[0].clientX);
                      };
                      
                      const handleTouchEnd = () => {
                        document.removeEventListener('touchmove', handleTouchMove);
                        document.removeEventListener('touchend', handleTouchEnd);
                      };
                      
                      document.addEventListener('touchmove', handleTouchMove, { passive: false });
                      document.addEventListener('touchend', handleTouchEnd);
                    }}
                  >
                    <div className="w-1 h-6 bg-white rounded-full opacity-80"></div>
                  </div>
                  
                  {/* Current time indicator */}
                  <div
                    className="absolute top-0 w-1 h-12 bg-red-500 rounded-full shadow-lg"
                    style={{ left: `${(currentTime / duration) * 100}%` }}
                  />
                  
                  {/* Time markers */}
                  <div className="absolute -bottom-6 left-0 right-0 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>0:00</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              ) : (
                <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onCancel}
                className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                انصراف
              </button>
              <button
                onClick={handleSave}
                disabled={isProcessing || trimmedDuration <= 0}
                className="flex-1 py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? 'در حال پردازش...' : 'ذخیره'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VideoTrimEditor;